import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";

const sections = [
  { key: "know", title: "I KNOW", hint: "What are you good at? (comma-separated)", placeholder: "e.g. Diesel engines, Espresso pulls" },
  { key: "teach", title: "I CAN TEACH", hint: "What can you help others learn?", placeholder: "e.g. Basic automotive maintenance" },
  { key: "learning", title: "I'M LEARNING", hint: "What are you currently learning?", placeholder: "e.g. Woodworking, Rust" },
  { key: "need_help", title: "I NEED HELP WITH", hint: "What's a problem or goal on your list?", placeholder: "e.g. Cabinet building" },
];

export default function Onboarding() {
  const [values, setValues] = useState({ know: "", teach: "", learning: "", need_help: "" });
  const [bio, setBio] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { refresh } = useAuth();

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding", {
        know: values.know.split(",").map(s => s.trim()).filter(Boolean),
        teach: values.teach.split(",").map(s => s.trim()).filter(Boolean),
        learning: values.learning.split(",").map(s => s.trim()).filter(Boolean),
        need_help: values.need_help.split(",").map(s => s.trim()).filter(Boolean),
        notify_topics: [],
        ai_preference: "auto",
        bio,
      });
      await refresh();
      toast.success("Profile ready");
      nav("/home");
    } catch (e) {
      toast.error("Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-xs font-mono-label text-[color:hsl(var(--amber))]">Onboarding · 1 of 1</div>
        <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl">Tell CLING what you know<br/>— and what you're curious about.</h1>
        <p className="mt-3 text-muted-foreground">Skip anything you're not ready to fill in. You can always update it later.</p>

        <div className="mt-8 space-y-6">
          {sections.map(({ key, title, hint, placeholder }) => (
            <div key={key} className="border rounded-2xl bg-card p-5" data-testid={`onboard-section-${key}`}>
              <div className="font-mono-label text-xs text-[color:hsl(var(--teal))]">{title}</div>
              <div className="text-sm text-muted-foreground mt-1">{hint}</div>
              <Input
                className="mt-3"
                value={values[key]}
                onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                placeholder={placeholder}
                data-testid={`onboard-input-${key}`}
              />
            </div>
          ))}
          <div className="border rounded-2xl bg-card p-5">
            <div className="font-mono-label text-xs text-[color:hsl(var(--teal))]">About you</div>
            <Textarea className="mt-3" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="A sentence or two about you (optional)" data-testid="onboard-bio" />
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <Button onClick={submit} disabled={busy} className="btn-teal h-11 rounded-full px-6" data-testid="onboard-submit">
            {busy ? "Saving..." : "Continue"}
          </Button>
          <Button variant="ghost" onClick={submit} disabled={busy}>Skip for now</Button>
        </div>
      </div>
    </div>
  );
}
